import * as React from 'react';

import { createPortal } from 'react-dom';

import { CloseIcon, ChevronIcon } from '@icons';

import * as Styled from './modal.styled';

type OwnProps = {
	hasOverlay?: boolean;
	isOpen: boolean;
	closeModal: () => void;
	icon?: React.ReactNode;
	hasCloseButton?: boolean;
	children?: React.ReactNode;
	title?: string | React.ReactNode;
	size?: Styled.ModalProps['$size'];
	titlePosition?: Styled.ModalProps['$titlePosition'];
	background?: string;
	backgroundColor?: Styled.ModalProps['$backgroundColor'];
	maxWidth?: number;
	hasShadow?: boolean;
	hasPadding?: boolean;
	isLoading?: boolean;
	onBackButtonClick?: boolean;
	noTitleBorder?: boolean;
};

const Modal: React.FC<OwnProps> = ({
	children,
	hasCloseButton = true,
	title = '',
	titlePosition = 'start',
	isOpen,
	closeModal,
	size = 'medium',
	background,
	backgroundColor = 'neutral',
	maxWidth,
	hasShadow,
	hasPadding = true,
	isLoading,
	onBackButtonClick,
	noTitleBorder = false
}) => {
	const modalRoot = document.getElementById('modal-root');

	const showHeader = title || background || hasCloseButton || onBackButtonClick || isLoading;

	const setModalClosed = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		closeModal();
	};

	if (!isOpen || !modalRoot) {
		return null;
	}

	return createPortal(
		<>
			<Styled.Overlay onClick={setModalClosed} $hasOverlay $backgroundColor={backgroundColor} />
			<Styled.ModalWrapper
				$size={size}
				$titlePosition={onBackButtonClick ? 'center' : titlePosition}
				$backgroundColor={backgroundColor}
				$maxWidth={maxWidth}
				$hasShadow={hasShadow}
				$hasPadding={hasPadding}
			>
				{showHeader ? (
					<Styled.ModalHeader $background={background}>
						{onBackButtonClick && (
							<Styled.BackButton onClick={closeModal}>
								<ChevronIcon />
							</Styled.BackButton>
						)}

						{typeof title === 'string' ? (
							<Styled.Title dangerouslySetInnerHTML={{ __html: title }} />
						) : (
							<Styled.Title>{title}</Styled.Title>
						)}

						{hasCloseButton && (
							<Styled.CloseButton onClick={setModalClosed}>
								<Styled.IconWrapper>
									<CloseIcon />
								</Styled.IconWrapper>
							</Styled.CloseButton>
						)}
					</Styled.ModalHeader>
				) : null}

				<Styled.ModalContent $noBorder={noTitleBorder} $hasHeader={!!showHeader}>
					{children}
				</Styled.ModalContent>
			</Styled.ModalWrapper>
		</>,
		modalRoot
	);
};

export { Modal };
